import { useRef, useEffect, useState, useCallback } from 'react'
import { getSocket } from '../../services/socket'
import { useAuthStore } from '../../stores/authStore'
import { Minimize2 } from 'lucide-react'
import type { Stroke, StrokeKind, StrokePoint, LaserPing } from './drawing/types'
import { renderStroke, renderLaser } from './drawing/strokeRenderers'
import { AnnotateToolbar } from './AnnotateToolbar'
import { dialog } from '../../services/dialog'
import { videoDisplayRect, normalizePoint } from './videoRect'

interface Point {
  x: number
  y: number
}

interface Props {
  channelId: string
  sessionId: string
  className?: string
  children: React.ReactNode
}

// OSPROD-53 + follow-up: per-room colour allocator that scales to
// any number of active drawers and keeps colours maximally distinct.
//
// Earlier revisions used a fixed 12-slot palette. That capped the
// canvas at 12 colours and — even with collision-walking — let two
// drawers land on near-duplicates (yellow+amber, red+rose, ...) which
// QA flagged as "approximately the same colour" in real sessions.
//
// New approach: distribute the N active drawers evenly around the
// OKLCH hue circle at fixed lightness (70%) and chroma (0.18). N
// drawers ⇒ N hues 360/N° apart, always unique by construction with
// no upper bound. OKLCH is perceptually uniform so equal hue spacing
// reads as equal hue distance and constant L reads as constant
// brightness across the wheel (HSL would need per-hue adjustment).
//
// Anchor: the hue of the lex-first userId is derived from its FNV-1a
// hash so different rooms get different rotations of the wheel and
// the same person isn't always assigned red.
//
// Determinism: every client computes the same active set (sorted
// unique userIds from the existing stroke + cursor broadcast) and
// runs the same allocator, so legend / canvas / cursor pills all
// agree without any server-side coordination.

// FNV-1a 32-bit, kept unsigned. Designed for short identifiers and
// avalanches every byte through the whole hash, so visually-similar
// UUIDs end up on far-apart hues.
const FNV_OFFSET_BASIS = 2166136261
const FNV_PRIME = 16777619
function hashUserIdToHue(userId: string): number {
  let h = FNV_OFFSET_BASIS
  for (let i = 0; i < userId.length; i++) {
    h = (h ^ userId.charCodeAt(i)) >>> 0
    h = Math.imul(h, FNV_PRIME) >>> 0
  }
  return h % 360
}

// L=70% C=0.18 looks vivid and readable on the dark canvas overlay
// across the entire hue circle. OKLCH is supported in Electron 30
// (Chromium 124) and all modern browsers we ship to.
function colourForHue(hue: number): string {
  const h = ((hue % 360) + 360) % 360
  return `oklch(70% 0.18 ${h.toFixed(2)})`
}

// Stable fallback for the rare unauthenticated case where userId is
// missing entirely. Same hue as the historical default (blue) so the
// "you" swatch doesn't change colour while the auth round-trip
// completes on a fresh page load.
const DEFAULT_HUE = 217
export const FALLBACK_COLOUR = colourForHue(DEFAULT_HUE)

// Per-user fallback used when a user is rendered *outside* the
// current active set — e.g. their live cursor expired but their old
// strokes are still on the canvas. Hashes the userId straight to a
// hue so the colour stays stable across re-renders.
export function colourForUser(userId: string): string {
  if (!userId) return FALLBACK_COLOUR
  return colourForHue(hashUserIdToHue(userId))
}

export function resolveUserColours(userIds: ReadonlyArray<string>): Record<string, string> {
  const unique = Array.from(new Set(userIds.filter(Boolean))).sort()
  const result: Record<string, string> = {}
  if (unique.length === 0) return result
  const anchor = hashUserIdToHue(unique[0])
  const step = 360 / unique.length
  for (let i = 0; i < unique.length; i++) {
    result[unique[i]] = colourForHue(anchor + i * step)
  }
  return result
}

// Live cursor: shows the drawer's name + colour swatch under their
// pointer while they're mid-stroke. Cleared when the stroke commits or
// goes idle for a few hundred ms (drawer left the canvas).
interface LiveCursor {
  userId: string
  username: string
  point: Point
  color: string
  expiresAt: number
}

interface LaserEntry {
  id: string
  x: number
  y: number
  color: string
  t: number
}

const CURSOR_LINGER_MS = 800

export default function ScreenShareAnnotation({ channelId, sessionId, className, children }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const drawCanvasRef = canvasRef // alias for clarity in save handler
  const [isDrawing, setIsDrawing] = useState(false)
  const [brushWidth, setBrushWidth] = useState(4)
  const [strokes, setStrokes] = useState<Stroke[]>([])
  const [currentStroke, setCurrentStroke] = useState<StrokePoint[]>([])
  const [cursors, setCursors] = useState<LiveCursor[]>([])
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [activeTool, setActiveTool] = useState<StrokeKind | 'laser'>('pen')
  const [shapeAnchor, setShapeAnchor] = useState<{ x: number; y: number } | null>(null)
  const [lasers, setLasers] = useState<LaserEntry[]>([])
  const [ownRedoCount, setOwnRedoCount] = useState(0)
  const lastLaserTsRef = useRef(0)
  const user = useAuthStore((s) => s.user)
  const socket = getSocket()
  const myId = user?.id || 'anonymous'
  const myName = user?.username || 'Anonymous'

  // Colour from localStorage (user-picked) or hash fallback
  const myHashColor = (() => {
    const allIds = [myId, ...strokes.map((s) => s.userId), ...cursors.map((c) => c.userId)]
    const colours = resolveUserColours(allIds)
    return colours[myId] ?? FALLBACK_COLOUR
  })()

  // OSPROD-205: colour is auto-assigned per user (the canvas recolours every
  // stroke by colourForUser on render), so there is no manual colour pick — the
  // active colour IS the user's hash colour. (Was a localStorage-backed picker
  // whose choice never affected the rendered colour.)
  const activeColor = myHashColor

  // OSPROD-53: per-room colour allocation. The active set covers the
  // viewer themselves plus everyone who's drawn a stroke or has a live
  // cursor on the canvas right now. resolveUserColours guarantees no
  // two of those collide; we still fall back to the plain hash for
  // strokes whose author left the active set (e.g. their cursor timed
  // out) so old strokes don't suddenly turn black.
  const userColours = resolveUserColours([
    myId,
    ...strokes.map((s) => s.userId),
    ...cursors.map((c) => c.userId),
  ])
  // Stable serialisation of the allocation so render effects can
  // depend on assignment changes without re-running on every parent
  // render (the userColours object identity changes each render).
  const userColoursKey = Object.keys(userColours)
    .sort()
    .map((k) => `${k}:${userColours[k]}`)
    .join('|')
  const myColor = userColours[myId] ?? FALLBACK_COLOUR
  const ownStrokeCount = strokes.filter((s) => s.userId === myId).length

  // Track fullscreen state
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // Emit voice:join-overlay on mount so server sends draw:replay
  useEffect(() => {
    socket.emit('voice:join-overlay', { channelId, sessionId })
  }, [channelId, sessionId, socket])

  // Resize canvas to match container
  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current
      const container = containerRef.current
      if (!canvas || !container) return
      const rect = container.getBoundingClientRect()
      canvas.width = rect.width
      canvas.height = rect.height
      redrawAll()
    }
    resize()
    const ro = new ResizeObserver(resize)
    if (containerRef.current) ro.observe(containerRef.current)
    return () => ro.disconnect()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strokes.length])

  const redrawAll = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const r = getVideoDisplayRect()
    if (!r) return
    const cw = r.width
    const ch = r.height

    // Translate context so renderer's normalized coords land in the letterbox rect
    ctx.save()
    ctx.translate(r.offsetX, r.offsetY)

    for (const stroke of strokes) {
      renderStroke({ ctx, stroke, canvasWidth: cw, canvasHeight: ch })
    }

    // Current in-progress freehand stroke (pen / highlighter / eraser)
    if (currentStroke.length > 1 && activeTool !== 'laser' && activeTool !== 'stamp' && activeTool !== 'text') {
      const isShapeTool = activeTool === 'line' || activeTool === 'arrow' || activeTool === 'rect' || activeTool === 'oval'
      if (!isShapeTool) {
        // freehand preview
        const previewStroke: Stroke = {
          strokeId: 'preview',
          channelId,
          sessionId,
          userId: myId,
          username: myName,
          color: activeColor,
          brushWidth,
          kind: activeTool as StrokeKind,
          points: currentStroke,
          timestamp: 0,
        }
        renderStroke({ ctx, stroke: previewStroke, canvasWidth: cw, canvasHeight: ch })
      } else if (shapeAnchor && currentStroke.length > 0) {
        // shape preview: anchor → current pointer
        const last = currentStroke[currentStroke.length - 1]
        const previewStroke: Stroke = {
          strokeId: 'preview',
          channelId,
          sessionId,
          userId: myId,
          username: myName,
          color: activeColor,
          brushWidth,
          kind: activeTool as StrokeKind,
          geometry: { x1: shapeAnchor.x, y1: shapeAnchor.y, x2: last.x, y2: last.y },
          timestamp: 0,
        }
        renderStroke({ ctx, stroke: previewStroke, canvasWidth: cw, canvasHeight: ch })
      }
    }

    // Laser dots
    const now = Date.now()
    for (const l of lasers) {
      renderLaser(ctx, l.x * cw, l.y * ch, l.color, now - l.t)
    }

    ctx.restore()
  // userColoursKey is the dep that captures assignment changes; the
  // userColours object is closed over from the enclosing render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strokes, currentStroke, myColor, brushWidth, userColoursKey, lasers, activeColor, activeTool, shapeAnchor, channelId, sessionId, myId, myName])

  useEffect(() => {
    redrawAll()
  }, [redrawAll])

  // Laser decay — requestAnimationFrame loop while any laser is alive
  useEffect(() => {
    if (lasers.length === 0) return
    let rafId: number
    const decay = () => {
      const now = Date.now()
      setLasers((prev) => prev.filter((l) => now - l.t < 1500))
      rafId = requestAnimationFrame(decay)
    }
    rafId = requestAnimationFrame(decay)
    return () => cancelAnimationFrame(rafId)
  }, [lasers.length])

  // Reap stale cursors so a drawer who left the canvas doesn't leave
  // their name floating forever. Cheap timer — only runs while there
  // are cursors.
  useEffect(() => {
    if (cursors.length === 0) return
    const t = setInterval(() => {
      const now = Date.now()
      setCursors((prev) => prev.filter((c) => c.expiresAt > now))
    }, 200)
    return () => clearInterval(t)
  }, [cursors.length])

  // Listen for remote strokes + remote cursors + lifecycle + new events.
  useEffect(() => {
    const handleRemoteStroke = (data: Stroke & { channelId: string }) => {
      if (data.channelId !== channelId) return
      if (data.sessionId && data.sessionId !== sessionId) return
      // Receiver enforces the per-user palette too: we don't trust the
      // colour the sender shipped (defence-in-depth — clients can lie),
      // we re-derive it from their userId so colours stay consistent
      // even if a malicious client tries to impersonate someone else.
      const stroke: Stroke = { ...data, color: colourForUser(data.userId) }
      setStrokes((prev) => [...prev, stroke])
      // Drop the live-cursor entry for this user — they just committed.
      setCursors((prev) => prev.filter((c) => c.userId !== data.userId))
    }
    const handleRemoteClear = (data: { channelId: string }) => {
      if (data.channelId !== channelId) return
      setStrokes([])
    }
    const handleRemoteUndo = (data: { channelId: string; userId: string }) => {
      if (data.channelId !== channelId) return
      // Undo only the LAST stroke from this user — no longer "the last
      // stroke globally". Matches the new own-strokes-only undo on the
      // sender side.
      setStrokes((prev) => {
        const idx = (() => {
          for (let i = prev.length - 1; i >= 0; i--) {
            if (prev[i].userId === data.userId) return i
          }
          return -1
        })()
        if (idx < 0) return prev
        return prev.slice(0, idx).concat(prev.slice(idx + 1))
      })
      // When we undo our own stroke, open a redo slot
      if (data.userId === myId) {
        setOwnRedoCount((c) => c + 1)
      }
    }
    const handleRemoteCursor = (data: { channelId: string; userId: string; username: string; point: Point }) => {
      if (data.channelId !== channelId) return
      if (!data.userId || data.userId === myId) return
      setCursors((prev) => {
        const next = prev.filter((c) => c.userId !== data.userId)
        next.push({
          userId: data.userId,
          username: data.username,
          point: data.point,
          color: colourForUser(data.userId),
          expiresAt: Date.now() + CURSOR_LINGER_MS,
        })
        return next
      })
    }
    const handleLaser = (data: LaserPing & { id?: string }) => {
      if (data.channelId !== channelId) return
      if (data.sessionId && data.sessionId !== sessionId) return
      setLasers((prev) => [
        ...prev,
        {
          id: data.id ?? crypto.randomUUID(),
          x: data.x,
          y: data.y,
          color: data.color,
          t: Date.now(),
        },
      ])
    }
    const handleReplay = (data: { channelId: string; sessionId: string; strokes: Stroke[] }) => {
      if (data.channelId !== channelId) return
      if (data.sessionId && data.sessionId !== sessionId) return
      setStrokes(data.strokes.map((s) => ({ ...s, color: colourForUser(s.userId) })))
    }
    const handleEvict = (data: { channelId: string; sessionId: string; strokeIds: string[] }) => {
      if (data.channelId !== channelId) return
      if (data.sessionId && data.sessionId !== sessionId) return
      const ids = new Set(data.strokeIds)
      setStrokes((prev) => prev.filter((s) => !ids.has(s.strokeId)))
    }
    socket.on('draw:stroke', handleRemoteStroke)
    socket.on('draw:clear', handleRemoteClear)
    socket.on('draw:undo', handleRemoteUndo)
    socket.on('draw:cursor', handleRemoteCursor)
    socket.on('draw:laser', handleLaser)
    socket.on('draw:replay', handleReplay)
    socket.on('draw:evict', handleEvict)
    return () => {
      socket.off('draw:stroke', handleRemoteStroke)
      socket.off('draw:clear', handleRemoteClear)
      socket.off('draw:undo', handleRemoteUndo)
      socket.off('draw:cursor', handleRemoteCursor)
      socket.off('draw:laser', handleLaser)
      socket.off('draw:replay', handleReplay)
      socket.off('draw:evict', handleEvict)
    }
  }, [channelId, sessionId, socket, myId])

  // Compute the rect occupied by the actual <video> pixels inside our
  // wrapper. Screen-share tiles render with object-fit: contain, so the
  // video is letterboxed — strokes need to live in *video* coordinates,
  // not wrapper coordinates, otherwise going fullscreen (which changes
  // the wrapper aspect ratio but not the video's) shifts every drawing
  // off the content it was annotating.
  const getVideoDisplayRect = (): { offsetX: number; offsetY: number; width: number; height: number } | null => {
    const container = containerRef.current
    if (!container) return null
    const cw = container.clientWidth
    const ch = container.clientHeight
    if (cw === 0 || ch === 0) return null
    const video = container.querySelector('video') as HTMLVideoElement | null
    return videoDisplayRect(cw, ch, video?.videoWidth || 0, video?.videoHeight || 0)
  }

  // Convert a normalised [0..1] point to canvas pixel coords accounting
  // for the letterbox.
  const pointToCanvas = (p: Point): { x: number; y: number } | null => {
    const r = getVideoDisplayRect()
    if (!r) return null
    return { x: r.offsetX + p.x * r.width, y: r.offsetY + p.y * r.height }
  }

  // Get normalized [0..1] point from a pointer/mouse event, relative to
  // the video letterbox rect.
  const getPoint = (e: React.MouseEvent | React.TouchEvent): Point | null => {
    const canvas = canvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return null
    const r = getVideoDisplayRect()
    if (!r) return null
    let clientX, clientY
    if ('touches' in e) {
      clientX = e.touches[0]?.clientX ?? e.changedTouches[0]?.clientX
      clientY = e.touches[0]?.clientY ?? e.changedTouches[0]?.clientY
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }
    return normalizePoint(clientX, clientY, { left: rect.left, top: rect.top }, r)
  }

  const handleMouseDown = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const point = getPoint(e)
    if (!point) return

    if (activeTool === 'text') {
      const value = await dialog.prompt({ title: 'Add text', message: 'Text', required: true })
      if (!value) return
      const stroke: Stroke = {
        strokeId: crypto.randomUUID(),
        channelId,
        sessionId,
        userId: myId,
        username: myName,
        color: activeColor,
        brushWidth,
        kind: 'text',
        text: { value, fontSize: 18, x: point.x, y: point.y },
        timestamp: 0,
      }
      setStrokes((prev) => [...prev, stroke])
      socket.emit('draw:stroke', stroke)
      setOwnRedoCount(0)
      return
    }

    if (activeTool === 'stamp') {
      const stroke: Stroke = {
        strokeId: crypto.randomUUID(),
        channelId,
        sessionId,
        userId: myId,
        username: myName,
        color: activeColor,
        brushWidth,
        kind: 'stamp',
        stampType: 'check',
        geometry: { x1: point.x, y1: point.y, x2: point.x, y2: point.y },
        timestamp: 0,
      }
      setStrokes((prev) => [...prev, stroke])
      socket.emit('draw:stroke', stroke)
      setOwnRedoCount(0)
      return
    }

    if (activeTool === 'laser') {
      socket.emit('draw:laser', { channelId, sessionId, userId: myId, color: activeColor, x: point.x, y: point.y })
      lastLaserTsRef.current = Date.now()
      setIsDrawing(true)
      return
    }

    const isShapeTool = activeTool === 'line' || activeTool === 'arrow' || activeTool === 'rect' || activeTool === 'oval'
    if (isShapeTool) {
      setShapeAnchor(point)
      setCurrentStroke([point])
      setIsDrawing(true)
      return
    }

    // pen / highlighter / eraser — freehand
    setIsDrawing(true)
    setCurrentStroke([point])
    socket.emit('draw:cursor', { channelId, sessionId, userId: myId, username: myName, point })
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing) return
    const point = getPoint(e)
    if (!point) return

    if (activeTool === 'laser') {
      const now = Date.now()
      if (now - lastLaserTsRef.current >= 33) { // ~30fps
        socket.emit('draw:laser', { channelId, sessionId, userId: myId, color: activeColor, x: point.x, y: point.y })
        lastLaserTsRef.current = now
      }
      return
    }

    const isShapeTool = activeTool === 'line' || activeTool === 'arrow' || activeTool === 'rect' || activeTool === 'oval'
    if (isShapeTool) {
      // Update current stroke tip for live preview
      setCurrentStroke([point])
      return
    }

    // freehand
    setCurrentStroke((prev) => [...prev, point])
    // Throttle cursor pings — emit every 4th sample.
    if (Math.random() < 0.25) {
      socket.emit('draw:cursor', { channelId, sessionId, userId: myId, username: myName, point })
    }
  }

  const handleMouseUp = () => {
    if (!isDrawing) {
      setIsDrawing(false)
      setCurrentStroke([])
      return
    }

    if (activeTool === 'laser') {
      setIsDrawing(false)
      return
    }

    const isShapeTool = activeTool === 'line' || activeTool === 'arrow' || activeTool === 'rect' || activeTool === 'oval'
    if (isShapeTool) {
      if (shapeAnchor && currentStroke.length > 0) {
        const last = currentStroke[currentStroke.length - 1]
        const stroke: Stroke = {
          strokeId: crypto.randomUUID(),
          channelId,
          sessionId,
          userId: myId,
          username: myName,
          color: activeColor,
          brushWidth,
          kind: activeTool,
          geometry: { x1: shapeAnchor.x, y1: shapeAnchor.y, x2: last.x, y2: last.y },
          timestamp: 0,
        }
        setStrokes((prev) => [...prev, stroke])
        socket.emit('draw:stroke', stroke)
        setOwnRedoCount(0)
      }
      setShapeAnchor(null)
      setIsDrawing(false)
      setCurrentStroke([])
      return
    }

    // freehand
    if (currentStroke.length < 2) {
      setIsDrawing(false)
      setCurrentStroke([])
      return
    }
    const stroke: Stroke = {
      strokeId: crypto.randomUUID(),
      channelId,
      sessionId,
      userId: myId,
      username: myName,
      color: activeColor,
      brushWidth,
      kind: activeTool as StrokeKind,
      points: currentStroke,
      timestamp: 0,
    }
    setStrokes((prev) => [...prev, stroke])
    socket.emit('draw:stroke', stroke)
    setOwnRedoCount(0)
    setIsDrawing(false)
    setCurrentStroke([])
  }

  // Undo only removes our OWN last stroke. Other people's strokes are
  // not touched — matches Zoom's behaviour and prevents griefing
  // ("everyone else just lost their work because Bob hit undo").
  const handleUndo = () => {
    setStrokes((prev) => {
      const idx = (() => {
        for (let i = prev.length - 1; i >= 0; i--) {
          if (prev[i].userId === myId) return i
        }
        return -1
      })()
      if (idx < 0) return prev
      return prev.slice(0, idx).concat(prev.slice(idx + 1))
    })
    socket.emit('draw:undo', { channelId, sessionId, userId: myId })
    setOwnRedoCount((c) => c + 1)
  }

  const handleRedo = () => {
    socket.emit('draw:redo', { channelId, sessionId, userId: myId })
    setOwnRedoCount((c) => Math.max(0, c - 1))
  }

  // Clear-all is intentionally global: any participant can wipe the
  // canvas. The host can use it to reset the slate before showing a
  // new demo without having to ask everyone to undo their own marks
  // one by one.
  const handleClear = () => {
    setStrokes([])
    socket.emit('draw:clear', { channelId, sessionId, userId: myId })
  }

  const handleSave = () => {
    const c = drawCanvasRef.current
    if (!c) return
    const a = document.createElement('a')
    a.href = c.toDataURL('image/png')
    a.download = `annotation-${new Date().toISOString().replace(/[:.]/g, '-')}.png`
    a.click()
  }

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {})
    }
  }, [])

  // Build the legend — one entry per unique user who has a stroke OR
  // a live cursor on the canvas. OSPROD-53: colours come from the
  // shared per-room allocation so the legend always agrees with the
  // strokes painted on the canvas, even after a collision-resolving
  // shift bumped someone off their hashed slot.
  const legend: Array<{ userId: string; username: string; color: string }> = (() => {
    const map = new Map<string, { userId: string; username: string; color: string }>()
    for (const s of strokes) {
      if (!map.has(s.userId)) {
        map.set(s.userId, {
          userId: s.userId,
          username: s.username,
          color: userColours[s.userId] ?? colourForUser(s.userId),
        })
      }
    }
    for (const c of cursors) {
      if (!map.has(c.userId)) {
        map.set(c.userId, {
          userId: c.userId,
          username: c.username,
          color: userColours[c.userId] ?? c.color,
        })
      }
    }
    return Array.from(map.values()).sort((a, b) => a.userId.localeCompare(b.userId))
  })()

  return (
    <div ref={containerRef} className={`relative ${className || ''}`}>
      {children}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full cursor-crosshair z-10"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />

      {/* Live drawer-name labels — shown at the cursor position while
          someone is mid-stroke. Each label is its own absolutely-
          positioned div on top of the canvas so it's crisp text rather
          than rasterised pixels (avoids fuzzing on resize / fullscreen). */}
      {cursors.map((c) => {
        const px = pointToCanvas(c.point)
        if (!px) return null
        // OSPROD-53: prefer the live allocation so the cursor pill
        // never disagrees with the legend or the stroke that's about
        // to land. Stored c.color survives as a fallback for the
        // brief window before the new userId enters userColours.
        const cursorColor = userColours[c.userId] ?? c.color
        return (
          <div
            key={c.userId}
            className="absolute z-20 pointer-events-none flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-white shadow-md whitespace-nowrap"
            style={{
              left: px.x + 8,
              top: px.y + 8,
              backgroundColor: cursorColor,
            }}
          >
            {c.username}
          </div>
        )
      })}

      {isFullscreen && (
        <button
          onClick={exitFullscreen}
          className="absolute top-3 right-3 z-30 w-9 h-9 rounded-lg bg-black/70 backdrop-blur-sm text-white hover:bg-black/90 flex items-center justify-center transition"
          title="Exit fullscreen (Esc)"
        >
          <Minimize2 size={18} />
        </button>
      )}

      {/* Toolbar — uses shared AnnotateToolbar with full toolset */}
      <div className={`absolute bottom-3 left-1/2 -translate-x-1/2 z-20 transition-opacity duration-200 ${isFullscreen ? 'opacity-100' : 'opacity-0 hover:opacity-100 group-hover:opacity-100'}`}>
        <AnnotateToolbar
          activeTool={activeTool}
          activeColor={activeColor}
          brushSize={brushWidth}
          canUndo={ownStrokeCount > 0}
          canRedo={ownRedoCount > 0}
          onSelectTool={setActiveTool}
          onBrushSize={setBrushWidth}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onSave={handleSave}
          onClear={handleClear}
        />
      </div>

      {/* Legend strip — bottom-left. Lists every drawer with their
          colour swatch + name so receivers can decode who drew what.
          Hidden until at least one person has drawn (or is drawing). */}
      {legend.length > 0 && (
        <div className={`absolute bottom-3 left-3 z-20 flex flex-wrap items-center gap-2 max-w-[40%] bg-black/60 backdrop-blur-sm rounded-lg px-2.5 py-1.5 transition-opacity duration-200 ${isFullscreen ? 'opacity-100' : 'opacity-0 hover:opacity-100 group-hover:opacity-100'}`}>
          {legend.map((l) => (
            <div key={l.userId} className="flex items-center gap-1.5 text-[11px] text-white/90">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: l.color }} />
              <span className="font-medium">{l.username}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
